import { useMutation, useQueryClient } from '@tanstack/react-query';
import { timeAttendanceService, type ClockInRequest, type ClockOutRequest, GeolocationError } from '@/services/timeAttendance';
import { timeAttendanceKeys } from './queryKeys';
import { useToast } from '@/hooks/use-toast';
import type { GeoErrorStatus } from '@/services/timeAttendance/types';

export interface GeofenceErrorDetails {
  geoStatus: GeoErrorStatus;
  message: string;
  distance?: number;
  allowedRadius?: number;
  locationName?: string;
  locationAddress?: string;
}

export const useTimeAttendanceMutations = (
  profileId: string,
  onGeofenceError?: (error: GeofenceErrorDetails) => void
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const clockInMutation = useMutation({
    mutationFn: async (request: ClockInRequest) => {
      const ipAddress = await timeAttendanceService.getUserIP();
      return timeAttendanceService.clockIn({
        ...request,
        ip_address_in: ipAddress,
      });
    },
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ 
        queryKey: timeAttendanceKeys.currentEntry(profileId) 
      });

      // Snapshot the previous value
      const previousEntry = queryClient.getQueryData(
        timeAttendanceKeys.currentEntry(profileId)
      );

      // Optimistically update to show clocked in state
      queryClient.setQueryData(
        timeAttendanceKeys.currentEntry(profileId),
        {
          entry: {
            id: 'temp-id',
            clock_in: new Date().toISOString(),
            clock_out: null,
            ip_address_in: null,
            ip_address_out: null,
            profile_id: profileId,
            company_id: '',
          },
          isLoggedIn: true,
        }
      );

      return { previousEntry };
    },
    onError: (err, variables, context) => {
      try {
        console.error('Clock in mutation error:', err);
        
        // Rollback on error
        if (context?.previousEntry) {
          queryClient.setQueryData(
            timeAttendanceKeys.currentEntry(profileId),
            context.previousEntry
          );
        }

        // Handle GeolocationError specially - route all geo errors to dialog
        if (err instanceof GeolocationError && err.geoStatus && onGeofenceError) {
          try {
            onGeofenceError({
              geoStatus: err.geoStatus as GeoErrorStatus,
              message: err.message,
              distance: err.distance,
              allowedRadius: err.allowedRadius,
              locationName: err.locationName,
              locationAddress: err.locationAddress,
            });
            return; // Don't show toast for geofence errors - dialog will handle it
          } catch (callbackErr) {
            console.error('Error in geofence error callback:', callbackErr);
            // Fallback to toast
            toast({
              title: 'Location Error',
              description: err.message,
              variant: 'destructive',
            });
            return;
          }
        }

        // Determine error message for other errors
        const errorMessage = err instanceof Error ? err.message : 'Failed to clock in';
        const isAuthError = errorMessage.includes('Authentication') || 
                            errorMessage.includes('Unauthorized') ||
                            errorMessage.includes('session');
        const isSchedulingError = errorMessage.includes('scheduled shift') ||
                                  errorMessage.includes('Unscheduled check-ins');

        toast({
          title: isAuthError ? 'Authentication Error' : 
                 isSchedulingError ? 'Check-in Not Allowed' : 
                 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      } catch (unexpectedError) {
        console.error('Unexpected error in onError handler:', unexpectedError);
        // Last resort fallback
        toast({
          title: 'Error',
          description: 'An unexpected error occurred',
          variant: 'destructive',
        });
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ 
        queryKey: timeAttendanceKeys.currentEntry(profileId) 
      });
      toast({
        title: 'Success',
        description: 'Successfully clocked in',
      });
    },
  });

  const clockOutMutation = useMutation({
    mutationFn: async (request: ClockOutRequest) => {
      const ipAddress = await timeAttendanceService.getUserIP();
      return timeAttendanceService.clockOut({
        ...request,
        ip_address_out: ipAddress,
      });
    },
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ 
        queryKey: timeAttendanceKeys.currentEntry(profileId) 
      });

      // Snapshot the previous value
      const previousEntry = queryClient.getQueryData(
        timeAttendanceKeys.currentEntry(profileId)
      );

      // Optimistically update to show clocked out state
      queryClient.setQueryData(
        timeAttendanceKeys.currentEntry(profileId),
        {
          entry: null,
          isLoggedIn: false,
        }
      );

      return { previousEntry };
    },
    onError: (err, variables, context) => {
      try {
        console.error('Clock out mutation error:', err);
        
        // Rollback on error
        if (context?.previousEntry) {
          queryClient.setQueryData(
            timeAttendanceKeys.currentEntry(profileId),
            context.previousEntry
          );
        }

        // Handle GeolocationError specially - route all geo errors to dialog
        if (err instanceof GeolocationError && err.geoStatus && onGeofenceError) {
          try {
            onGeofenceError({
              geoStatus: err.geoStatus as GeoErrorStatus,
              message: err.message,
              distance: err.distance,
              allowedRadius: err.allowedRadius,
              locationName: err.locationName,
              locationAddress: err.locationAddress,
            });
            return; // Don't show toast for geofence errors - dialog will handle it
          } catch (callbackErr) {
            console.error('Error in geofence error callback:', callbackErr);
            // Fallback to toast
            toast({
              title: 'Location Error',
              description: err.message,
              variant: 'destructive',
            });
            return;
          }
        }

        // Determine error message for other errors
        const errorMessage = err instanceof Error ? err.message : 'Failed to clock out';
        const isAuthError = errorMessage.includes('Authentication') || 
                            errorMessage.includes('Unauthorized') ||
                            errorMessage.includes('session');
        const isSchedulingError = errorMessage.includes('scheduled shift') ||
                                  errorMessage.includes('Unscheduled check-ins');

        toast({
          title: isAuthError ? 'Authentication Error' : 
                 isSchedulingError ? 'Check-in Not Allowed' : 
                 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      } catch (unexpectedError) {
        console.error('Unexpected error in onError handler:', unexpectedError);
        // Last resort fallback
        toast({
          title: 'Error',
          description: 'An unexpected error occurred',
          variant: 'destructive',
        });
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ 
        queryKey: timeAttendanceKeys.currentEntry(profileId) 
      });
      toast({
        title: 'Success',
        description: 'Successfully clocked out',
      });
    },
  });

  return {
    clockIn: clockInMutation,
    clockOut: clockOutMutation,
  };
};
